import {inject, injectable} from 'inversify';

import type {ConferenceExpandedCommon} from '../../../../api/conference';
import type {EventApiModel} from '../../../../api/event';
import type {TaskExpandedCommon} from '../../../../api/task';
import type {IApiDataMapper} from '../../../ports/types';
import type {AppEventModel} from '../AppEvent.model';

import {not} from '../../../../utils/conditionalUtils';
import {getEntityTypeById} from '../../../../utils/entity/getEntityType';
import {ConferenceMapperJrpc} from '../../Conference/mappers/Conference.mapper.jrpc';
import {TaskMapperJrpc} from '../../Task/mappers/Task.mapper.jrpc';
import {UserMapperJrpc} from '../../User/mappers/User.mapper.jrpc';
import {AppEventFactory} from '../AppEvent.factory';
import {AppEventExpandJrpc} from './AppEvent.expand.jrpc';

@injectable()
export class AppEventMapperJrpc
  implements
    IApiDataMapper<
      AppEventMapperJrpc.ApiData,
      AppEventModel.Props,
      AppEventModel
    >
{
  private SUPPORTED_CONTEXTS = [
    'course',
    'task',
    'conference',
    'material',
    'coach_history',
  ];

  constructor(
    @inject(AppEventFactory) private readonly appEventFactory: AppEventFactory,
    @inject(ConferenceMapperJrpc)
    private readonly conferenceMapper: ConferenceMapperJrpc,
    @inject(TaskMapperJrpc)
    private readonly taskMapper: TaskMapperJrpc,
    @inject(UserMapperJrpc) private readonly userMapper: UserMapperJrpc,
    @inject(AppEventExpandJrpc)
    public readonly expand: AppEventExpandJrpc,
  ) {}

  mapDataToModel(apiData: AppEventMapperJrpc.ApiData): AppEventModel {
    const props = this.mapDataToProps(apiData);

    return this.appEventFactory.create(props);
  }

  mapDataToProps(apiData: AppEventMapperJrpc.ApiData): AppEventModel.Props {
    const id = apiData._id;
    const type = this.type(apiData);
    const user = this.user(apiData);
    const actor = this.actor(apiData);
    const context = this.context(apiData);

    return {
      actor,
      apiData,
      context,
      id,
      type,
      user,
    };
  }

  private actor(props: AppEventMapperJrpc.ApiData): AppEventModel['actor'] {
    return this.userMapper.mapDataToModel(props.actor);
  }

  private context(props: AppEventMapperJrpc.ApiData): AppEventModel['context'] {
    const context = props.context;

    if (!context) {
      //eslint-disable-next-line no-console
      console.log('NO CONTEXT IN EVENT CONTEXT FACTORY. empty props');

      return null;
    }

    const entityType = getEntityTypeById(props._id) ?? '';

    if (not(this.isSupportedContext(entityType))) {
      return null;
    }

    switch (entityType) {
      case 'conference':
        return this.conferenceMapper.mapDataToModel(
          context as ConferenceExpandedCommon,
        );
      case 'task':
        return this.taskMapper.mapDataToModel(context as TaskExpandedCommon);
      // case 'course':
      //     return this.Course.create(props as ApiCourseData);
      // case 'material':
      //     return this.Material.create(props as ApiMaterialData);
      // case 'coach_history':
      //     return this.CoachCoverage.create(props as ApiCoachCoverageData);
      default:
        throw 'UNKNOWN CONTEXT TYPE IN EVENT CONTEXT FACTORY';
    }
  }

  private isSupportedContext(type: string) {
    return this.SUPPORTED_CONTEXTS.includes(type);
  }

  private type(props: AppEventMapperJrpc.ApiData): AppEventModel['type'] {
    return props.type as AppEventModel.SupportedType;
  }

  private user(props: AppEventMapperJrpc.ApiData): AppEventModel['user'] {
    return this.userMapper.mapDataToModel(props.user);
  }
}

export namespace AppEventMapperJrpc {
  export type ApiContextData =
    | ConferenceMapperJrpc.ApiData
    | null
    // | ApiMaterialData
    // | ApiCourseData
    // | ApiCoachCoverageData
    | TaskMapperJrpc.ApiData;

  export type ApiData = EventApiModel & {
    actor: UserMapperJrpc.ExpandedApiData;
    context: ApiContextData;
    user: UserMapperJrpc.ExpandedApiData;
  };
}
